import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatMinorUsd, type AdminInvoice } from '@fois-utsavvoice/shared';
import { listInvoices } from '../api/invoices.js';
import { useAuth } from '../auth/AuthContext.js';
import { AdminNav } from '../components/AdminNav.js';
import { PageShell } from '../components/PageShell.js';

export function InvoiceListPage() {
  const auth = useAuth();
  const [items, setItems] = useState<AdminInvoice[]>([]);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!auth.tokens) {
      return;
    }
    listInvoices(auth.tokens.idToken)
      .then((page) => setItems(page.items))
      .catch(() => setError('Invoices could not be loaded.'));
  }, [auth.tokens]);

  return (
    <PageShell>
      <AdminNav />
      <section className="card">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="heading-xl text-crimson">Invoices</h1>
          <Link className="btn-primary" to="/admin/invoices/new">
            Create invoice
          </Link>
        </div>
        <p className="mt-3 text-sm text-charcoal/80">
          Manual invoices must be reconciled with Shopify for inventory and tax. Issued invoices
          cannot be edited.
        </p>
        {error ? (
          <p className="mt-4 text-sm font-semibold text-crimson" role="alert">
            {error}
          </p>
        ) : null}
        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-linen">
                <th className="py-2 pr-4">Number</th>
                <th className="py-2 pr-4">Customer</th>
                <th className="py-2 pr-4">Source</th>
                <th className="py-2 pr-4">Total</th>
                <th className="py-2 pr-4">Balance</th>
                <th className="py-2 pr-4">Issue date</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Last sent</th>
                <th className="py-2"> </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.invoiceId} className="border-b border-linen">
                  <td className="py-3 pr-4">{item.invoiceNumber ?? 'DRAFT'}</td>
                  <td className="py-3 pr-4">{item.customer.name}</td>
                  <td className="py-3 pr-4">{item.sourceOrderNumber ?? item.sourceType}</td>
                  <td className="py-3 pr-4">{formatMinorUsd(item.totalMinor)}</td>
                  <td className="py-3 pr-4">{formatMinorUsd(item.balanceDueMinor)}</td>
                  <td className="py-3 pr-4">{item.issueDate ?? '—'}</td>
                  <td className="py-3 pr-4">{item.status}</td>
                  <td className="py-3 pr-4">
                    {item.lastSentAt ? new Date(item.lastSentAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-3">
                    <Link
                      className="font-semibold text-crimson underline"
                      to={`/admin/invoices/${item.invoiceId}`}
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && !error ? (
            <p className="mt-4 text-charcoal/80">No invoices yet.</p>
          ) : null}
        </div>
      </section>
    </PageShell>
  );
}
